"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Header } from "./header";
import Link from "next/link";
import { Poster } from "./poster";
import type { CatalogResponse, CatalogTitle } from "@/lib/dramahub";
import { watchPath } from "@/lib/title-url";

export function Browse() {
  const [selection, setSelection] = useState({ platform: "", lang: "" });
  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [searchError, setSearchError] = useState("");
  useEffect(() => {
    const focusSearch = () => {
      const query = new URLSearchParams(window.location.search).get("q")?.trim() ?? "";
      const value = Array.from(query).slice(0, 100).join("");
      setKeyword(value.length >= 2 ? value : "");
      setSearchInput(value);
      setSearchError("");
      if (window.location.hash === "#search") {
        document.getElementById("search")?.focus();
        window.scrollTo({ top: 0 });
      }
    };
    focusSearch();
    window.addEventListener("hashchange", focusSearch);
    return () => window.removeEventListener("hashchange", focusSearch);
  }, []);
  const onSelectionChange = useCallback((platform: string, lang: string) => {
    setSelection({ platform, lang });
  }, []);

  return <>
    <Header onSelectionChange={onSelectionChange}>
      <form className="search-form" role="search" onSubmit={(event) => {
        event.preventDefault();
        const value = Array.from(searchInput.trim()).slice(0, 100).join("");
        if (value && Array.from(value).length < 2) { setSearchError("Masukkan minimal 2 karakter."); return; }
        setSearchError("");
        setKeyword(value);
      }}>
        <label className="sr-only" htmlFor="search">Cari judul</label>
        <input id="search" type="search" placeholder="Cari cerita favoritmu..." value={searchInput} onChange={(event) => { setSearchInput(event.target.value); setSearchError(""); }} aria-describedby="search-status" aria-invalid={!!searchError} />
        <button className="catalog-button" type="submit">Cari</button>
        {keyword && <button className="catalog-button" type="button" onClick={() => { setKeyword(""); setSearchInput(""); setSearchError(""); }}>Reset</button>}
      </form>
    </Header>
    <main className="catalog-main" aria-label="Katalog Dracin">
      <div className="catalog-heading">
        <div><p className="eyebrow">TEMUKAN CERITAMU</p><h1>Jelajahi katalog<span>.</span></h1></div>
        {selection.platform && <span className="catalog-source">{selection.platform}{selection.lang && ` / ${selection.lang.toUpperCase()}`}</span>}
      </div>
      <p id="search-status" className={searchError || keyword ? "search-status" : "sr-only"} role="status">{searchError || (keyword ? `Hasil pencarian untuk "${keyword}"` : "")}</p>
      {selection.platform && selection.lang
        ? selection.platform === "vibeshort" && !keyword
          ? <VibeHome key={`${selection.platform}:${selection.lang}`} lang={selection.lang} />
          : <Catalog key={JSON.stringify([selection.platform, selection.lang, keyword])} {...selection} keyword={keyword} />
        : <div className="catalog-message" role="status">Pilih platform dan bahasa yang tersedia untuk melihat katalog.</div>}
    </main>
  </>;
}

function Catalog({ platform, lang, keyword }: { platform: string; lang: string; keyword: string }) {
  const [netTabs, setNetTabs] = useState<{ id: string; name: string; isDefault?: boolean }[]>([]);
  const [activeTab, setActiveTab] = useState("");
  useEffect(() => {
    if (platform !== "netshort" || keyword) { setNetTabs([]); setActiveTab(""); return; }
    const controller = new AbortController();
    void fetch(`/api/tabs?platform=netshort&lang=${encodeURIComponent(lang)}`, { signal: controller.signal }).then((response) => response.json()).then((body) => {
      if (body.success) { setNetTabs(body.data); setActiveTab(body.data.find((tab: { isDefault?: boolean }) => tab.isDefault)?.id ?? body.data[0]?.id ?? ""); }
    }).catch(() => {});
    return () => controller.abort();
  }, [platform, lang, keyword]);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [section, setSection] = useState(platform === "shortmax" ? "recommend" : "rank");

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(false);
      setResult(null);
      try {
        const query = new URLSearchParams({ platform, lang, page: String(page), limit: "24" });
        if ((platform === "melolo" || platform === "shortmax") && !keyword) query.set("section", section);
        if (platform === "netshort" && !keyword && activeTab) query.set("tab", activeTab);
        if (keyword) query.set("q", keyword);
        const response = await fetch(`/api/${keyword ? "search" : "catalog"}?${query}`, { signal: controller.signal });
        const body = await response.json();
        if (!response.ok || !body.success) throw new Error("Catalog unavailable");
        if (!controller.signal.aborted) setResult(body);
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => controller.abort();
  }, [platform, lang, keyword, page, attempt, section, activeTab]);

  function changePage(nextPage: number) {
    setLoading(true);
    setPage(nextPage);
    document.querySelector(".catalog-heading")?.scrollIntoView({ block: "start" });
  }

  const hasNext = !!result && (result.meta.has_next ?? result.data.length >= result.meta.limit) && page < 10000;
  return <section aria-label="Daftar judul" aria-busy={loading}>
    {platform === "netshort" && !keyword && netTabs.length > 0 && <div className="vibe-tabs" role="tablist" aria-label="Kategori NetShort">{netTabs.map((tab) => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => { setActiveTab(tab.id); setPage(1); }}>{tab.name}</button>)}</div>}
    {platform === "shortmax" && !keyword && <div className="vibe-tabs" role="tablist" aria-label="Bagian ShortMax">{[["recommend", "Rekomendasi"], ["new", "Baru"], ["ranked", "Peringkat"], ["epic", "Dunia Epic"], ["foryou", "For You"]].map(([value, label]) => <button key={value} role="tab" aria-selected={section === value} onClick={() => { setSection(value); setPage(1); }}>{label}</button>)}</div>}
    {platform === "melolo" && !keyword && <div className="vibe-tabs" role="tablist" aria-label="Bagian Melolo">{[["rank", "Ranking"], ["anime", "Anime"]].map(([value, label]) => <button key={value} role="tab" aria-selected={section === value} onClick={() => { setSection(value); setPage(1); }}>{label}</button>)}</div>}
    {loading ? <>
      <p className="sr-only" role="status">Memuat katalog...</p>
      <div className="catalog-grid" aria-hidden="true">{Array.from({ length: 24 }, (_, index) => <div className="skeleton-card" key={index}><div /><span /></div>)}</div>
    </> : error ? <div className="catalog-message" role="alert"><p>Katalog belum dapat dimuat.</p><button className="catalog-button" onClick={() => setAttempt((value) => value + 1)}>Coba lagi</button></div>
      : result?.data.length ? <div className="catalog-grid">{result.data.map((item) => <TitleCard key={`${item.id}:${item.lang}`} item={item} />)}</div>
        : <div className="catalog-message" role="status">{page === 1 ? (keyword ? "Tidak ada judul yang cocok. Coba kata kunci lain." : "Belum ada judul untuk pilihan ini.") : "Tidak ada judul lagi di halaman ini."}</div>}
    <nav className="pagination" aria-label="Halaman katalog">
      <button className="catalog-button" disabled={loading || page <= 1} onClick={() => changePage(page - 1)}>Sebelumnya</button>
      <span role="status">Halaman {page}{!loading && result ? ` / ${result.data.length} judul` : ""}</span>
      <button className="catalog-button" disabled={loading || error || !hasNext} onClick={() => changePage(page + 1)}>Berikutnya</button>
    </nav>
  </section>;
}

function TitleCard({ item }: { item: CatalogTitle }) {
  return <article className="title-card">
    <Link href={`${watchPath(item.id)}?${new URLSearchParams({ lang: item.lang })}`} prefetch={false}>
    <div className="poster">
      <Poster src={item.poster} title={item.title} />
      {item.episode_count !== null && <span className="episode-badge">{item.episode_count} episode</span>}
    </div>
    <h2 title={item.title}>{item.title}</h2>
    <p className="title-meta">{item.platform} <span>{item.lang.toUpperCase()}</span></p>
    </Link>
  </article>;
}


function VibeHome({ lang }: { lang: string }) {
  const [feedPage, setFeedPage] = useState(1);
  const [classPage, setClassPage] = useState(1);
  const [classId, setClassId] = useState<number | undefined>();
  const [data, setData] = useState<{ ranking: CatalogTitle[]; classes: { id: number; name: string }[]; feed: CatalogTitle[]; classFeed: CatalogTitle[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);


  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    fetch(`/api/vibeshort/home?${new URLSearchParams({ lang, page: String(feedPage), classPage: String(classPage), pageSize: "24", ...(classId ? { classId: String(classId) } : {}) })}`, { signal: controller.signal })
      .then(async (response) => { const body = await response.json(); if (!response.ok || !body.success) throw new Error(); return body.data; })
      .then((value) => { if (!controller.signal.aborted) setData(value); })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [lang, feedPage, classPage, classId, retry]);

  function chooseClass(id?: number) { setClassId(id); setClassPage(1); }
  return <div className="vibe-home">
    <section className="vibe-ranking" aria-label="Peringkat VibeShort">
      <div className="vibe-ranking-heading"><div className="catalog-heading"><div><p className="eyebrow">PALING DIBICARAKAN</p><h2>Peringkat<span>.</span></h2></div><span className="catalog-source">VibeShort</span></div></div>
      <RankingSlider items={data?.ranking ?? []} />
    </section>
    <section className="vibe-feed" aria-label="For You VibeShort">
      <div className="catalog-heading"><div><p className="eyebrow">DIPILIHKAN UNTUKMU</p><h2>For You<span>.</span></h2></div></div>
      {error ? <div className="catalog-message" role="alert">Feed belum dapat dimuat. <button className="catalog-button" onClick={() => setRetry((value) => value + 1)}>Coba lagi</button></div> : loading ? <div className="catalog-message" role="status">Memuat rekomendasi...</div> : <div className="catalog-grid">{(data?.feed ?? []).map((item) => <TitleCard key={item.id} item={item} />)}</div>}
      <Pager page={feedPage} loading={loading} count={data?.feed.length ?? 0} onPage={setFeedPage} />
    </section>
    <section className="vibe-classes" aria-label="Drama berdasarkan kelas">
      <div className="catalog-heading"><div><p className="eyebrow">PILIH SUASANA</p><h2>Jelajahi kelas<span>.</span></h2></div></div>
      <div className="vibe-tabs" role="tablist" aria-label="Kelas drama"><button role="tab" aria-selected={!classId} onClick={() => chooseClass()}>For You</button>{(data?.classes ?? []).map((item) => <button role="tab" aria-selected={classId === item.id} key={item.id} onClick={() => chooseClass(item.id)}>{item.name}</button>)}</div>
      {error ? <div className="catalog-message" role="alert">Drama kelas belum dapat dimuat. <button className="catalog-button" onClick={() => setRetry((value) => value + 1)}>Coba lagi</button></div> : <div className="catalog-grid">{(data?.classFeed ?? []).map((item) => <TitleCard key={item.id} item={item} />)}</div>}
      <Pager page={classPage} loading={loading} count={data?.classFeed.length ?? 0} onPage={setClassPage} />
    </section>
  </div>;
}

function Pager({ page, loading, count, onPage }: { page: number; loading: boolean; count: number; onPage: (page: number) => void }) {
  return <nav className="pagination" aria-label="Halaman feed"><button className="catalog-button" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>Sebelumnya</button><span>Halaman {page}</span><button className="catalog-button" disabled={loading || count < 24} onClick={() => onPage(page + 1)}>Berikutnya</button></nav>;
}
function RankingSlider({ items }: { items: CatalogTitle[] }) {
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;

  function stepSize() {
    const cards = track.current?.querySelectorAll<HTMLElement>(".vibe-rank-card");
    if (!cards || cards.length < 2) return 0;
    return cards[1].offsetLeft - cards[0].offsetLeft;
  }
  function move(direction: number) {
    const step = stepSize();
    if (step) track.current?.scrollBy({ left: step * direction, behavior: "smooth" });
  }

  useEffect(() => {
    if (count < 2 || paused) return;
    const timer = window.setInterval(() => move(1), 4500);
    return () => window.clearInterval(timer);
  }, [count, paused]);

  useEffect(() => {
    if (!count || !track.current) return;
    const step = stepSize();
    track.current.scrollLeft = step * count;
    setActive(0);
  }, [count]);

  function syncActive() {
    const element = track.current;
    const step = stepSize();
    if (!element || !step || !count) return;
    const position = Math.round(element.scrollLeft / step);
    setActive(((position - count) % count + count) % count);
    if (position < count || position >= count * 2) {
      const centered = position < count ? position + count : position - count;
      requestAnimationFrame(() => { if (track.current) track.current.scrollLeft = centered * step; });
    }
  }

  return <div className="vibe-slider-wrap" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false); }}>
    <button className="vibe-slider-arrow previous" aria-label="Peringkat sebelumnya" onClick={() => move(-1)}>←</button>
    <div className="vibe-ranking-track" ref={track} onScroll={syncActive}>
      {[...items, ...items, ...items].map((item, index) => <article className={`vibe-rank-card${index % count === active ? " is-highlight" : ""}`} key={`${Math.floor(index / Math.max(1, count))}:${item.id}`}><span className="vibe-rank-number">{String(index % Math.max(1, count) + 1).padStart(2, "0")}</span><TitleCard item={item} /></article>)}
    </div>
    <button className="vibe-slider-arrow next" aria-label="Peringkat berikutnya" onClick={() => move(1)}>→</button>
  </div>;
}